/**
 * Drop-downs with one choice (MB's R06, 24 Oct 2026): on a new record, a drop-down that offers exactly one choice
 * fills itself (the job's one aircraft, its one pilot, its one block). A value already chosen is never replaced, and
 * an optional second slot (pilot 2, operator 2) is never filled: the one pilot is the first pilot.
 */
import type { Aircraft, Block, DailyEntry, Person, Position, ProjectCrew } from './model.js';
import { crewChoices } from './staff.js';

/** The only choice of a list that has exactly one, else null. */
export function onlyChoice<T>(choices: readonly T[]): T | null {
  return choices.length === 1 ? choices[0]! : null;
}

const empty = (v: unknown): boolean => v === null || v === undefined || v === '';

/**
 * Each empty field whose drop-down offers exactly one choice takes that choice; every other field is kept as it is.
 * `choices` lists, per field, the values its drop-down offers (the blank "–" not counted).
 */
export function fillSingleChoices<T extends object>(
  record: T,
  choices: { readonly [K in keyof T]?: readonly NonNullable<T[K]>[] },
): T {
  let out = record;
  for (const key of Object.keys(choices) as (keyof T)[]) {
    const one = onlyChoice(choices[key] ?? []);
    if (one === null || !empty(record[key])) continue;
    out = { ...out, [key]: one };
  }
  return out;
}

/** What a daily entry's drop-downs offer. */
export interface DailyEntryChoices {
  readonly aircraft: readonly Pick<Aircraft, 'id'>[];
  readonly people: readonly Person[];
  readonly crew: readonly ProjectCrew[];
  readonly positions: readonly Pick<Position, 'id' | 'kind'>[];
  readonly blocks: readonly Pick<Block, 'id'>[];
}

/** Operator 1's list for a fill (R110): the project's crew on the entry's date when it has operators, else the register's. */
function operatorOneChoices(
  entry: Pick<DailyEntry, 'date'>,
  c: Pick<DailyEntryChoices, 'people' | 'crew'>,
): string[] {
  const operators = crewChoices(c.people, 'operator', null, { crew: c.crew, date: entry.date || null });
  const onJob = operators.filter((o) => o.group === 'project');
  return (onJob.length > 0 ? onJob : operators).map((o) => o.id);
}

const baseMagChoices = (c: Pick<DailyEntryChoices, 'positions'>): string[] =>
  c.positions.filter((p) => p.kind === 'base_mag').map((p) => p.id);

/**
 * Operator 1 and the base mag location of a flight entry, each from its one choice (MB's R241, R242, 5 Nov 2026; the
 * rule of `fillNewDailyEntry`), for the moments after a new entry opens and for a flight started on a phone. Never
 * over a value: a box with someone or somewhere in it, the kept name of a person removed for good, or a box in
 * `changed`, the fields the person has changed on this card (a box they emptied stays empty).
 */
export function fillOperatorAndBaseMag<
  E extends Pick<DailyEntry, 'date' | 'operator1_id' | 'base_mag_position_id' | 'operator1_name'>,
>(
  entry: E,
  c: Pick<DailyEntryChoices, 'people' | 'crew' | 'positions'>,
  changed: ReadonlySet<PropertyKey> = new Set(),
): E {
  const operator =
    entry.operator1_id === null && !entry.operator1_name && !changed.has('operator1_id')
      ? onlyChoice(operatorOneChoices(entry, c))
      : null;
  const baseMag =
    entry.base_mag_position_id === null && !changed.has('base_mag_position_id')
      ? onlyChoice(baseMagChoices(c))
      : null;
  if (operator === null && baseMag === null) return entry;
  return {
    ...entry,
    ...(operator !== null ? { operator1_id: operator } : {}),
    ...(baseMag !== null ? { base_mag_position_id: baseMag } : {}),
  };
}

/**
 * A new daily entry with its one-choice drop-downs filled: the aircraft, the pilot, operator 1, the base mag, ground
 * cal and test line locations and the default block; with one block, "Blocks touched" ticks it too. Pilot and
 * operator choices are the staff register's for the entry's date, as the drop-downs list them.
 *
 * Operator 1 (MB's R110, 27 Oct 2026): when the project's crew on the entry's date has operators, the choice is
 * among them (the drop-down's "On this project" group), so the one operator on the job fills it however many the
 * register holds; with no operator on the crew, the register's one operator, as before.
 */
export function fillNewDailyEntry<E extends DailyEntry>(entry: E, c: DailyEntryChoices): E {
  const ids = (xs: readonly { readonly id: string }[]): string[] => xs.map((x) => x.id);
  const project = { crew: c.crew, date: entry.date || null };
  const at = (kind: Position['kind']): string[] => ids(c.positions.filter((p) => p.kind === kind));
  const filled = fillSingleChoices<DailyEntry>(entry, {
    aircraft_id: ids(c.aircraft),
    pilot_id: ids(crewChoices(c.people, 'pilot', null, project)),
    operator1_id: operatorOneChoices(entry, c),
    base_mag_position_id: baseMagChoices(c),
    ground_cal_position_id: at('ground_cal'),
    test_line_position_id: at('test_line'),
    default_block_id: ids(c.blocks),
  }) as E;
  const block = onlyChoice(ids(c.blocks));
  return block !== null && filled.blocks.length === 0 ? { ...filled, blocks: [block] } : filled;
}

/**
 * The person a project crew "add" panel shows for `role` (decision 524, Q463 point 6 B): the one chosen, else the
 * only person on offer (`choices`: the active people holding the role) who is not already on the crew in that role
 * with no end date, else '' ("–", the person chooses). A person's own choice is never replaced.
 */
export function crewAddPerson(
  choices: readonly Pick<Person, 'id'>[],
  crew: readonly ProjectCrew[],
  role: ProjectCrew['role'],
  chosen: string,
): string {
  if (chosen) return chosen;
  const on = new Set(
    crew
      .filter((c) => c.deleted_at === null && c.role === role && c.end_date === null)
      .map((c) => c.person_id),
  );
  return onlyChoice(choices.filter((p) => !on.has(p.id)))?.id ?? '';
}
